import { Controller, useFormContext } from 'react-hook-form';
import { Input, TmdxTextarea } from '@ds';
import { FormSection } from '@features/components/CaseFormBlocks/components';
import { DonorFormRow } from './DonorFormRow';
import { CtScanRow } from './CtScanRow';
import { MatchIdRow } from './MatchIdRow';
import { ReasonForUnallocatedRow } from './ReasonForUnallocatedRow';
import { DonorProfileFormValues } from '../types';
import { createOrganField, OrganAdditionalInfoSectionProps } from '../organField';

const UnitLabel = ({ unit }: { unit: string }) => (
  <span className="text-base-30 text-m-r pointer-events-none select-none">{unit}</span>
);

export const LungsAdditionalInfoSection = ({
  namePrefix,
  title = 'Additional Information',
  showReasonForUnallocated = true,
  showMatchId = false,
}: OrganAdditionalInfoSectionProps) => {
  const { control } = useFormContext<DonorProfileFormValues>();
  const organField = createOrganField(namePrefix);

  return (
    <FormSection title={title}>
      {showMatchId && <MatchIdRow namePrefix={namePrefix} />}
      {showReasonForUnallocated && <ReasonForUnallocatedRow />}

      <DonorFormRow title="List of OCS Centers on Match Run" multiRow>
        <Controller
          name={organField('ocsMatchRunCenters')}
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <TmdxTextarea
              placeholder="Enter centers"
              aria-label="List of OCS Centers on Match Run"
              maxLength={128}
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
      </DonorFormRow>

      <DonorFormRow title="Most Recent PF Ratio">
        <div className="w-[332px]">
          <Controller
            name={organField('pfRatio')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                placeholder="Enter value"
                aria-label="Most Recent PF Ratio"
                endContent={<UnitLabel unit="mmHg" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="24 hr Fluid Balance">
        <div className="w-[332px]">
          <Controller
            name={organField('fluidBalance24hr')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                step="1"
                placeholder="Enter value"
                aria-label="24 hr Fluid Balance"
                endContent={<UnitLabel unit="mL" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="Culture Sensitivities" multiRow>
        <Controller
          name={organField('cultureSensitivities')}
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <TmdxTextarea
              placeholder="Enter culture sensitivities"
              aria-label="Culture Sensitivities"
              maxLength={128}
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
      </DonorFormRow>

      <CtScanRow namePrefix={namePrefix} />

      <DonorFormRow title="Sedation" multiRow>
        <Controller
          name={organField('sedation')}
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <Input
              placeholder="Enter sedation details"
              aria-label="Sedation"
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
      </DonorFormRow>
    </FormSection>
  );
};
